import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Plus, Users, KeyRound, ShieldPlus, Power, PowerOff, Lock, X, Search } from 'lucide-react';
import companyAdminService from '../../services/companyAdminService';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS, ROLE_NAMES } from '../../constants/permissions';
import { PARTNER_ADMIN_COPY, ROLE_COPY } from '../../constants/partnerCopy';
import { mapApiError } from '../../utils/apiError';
import ApiKeyRevealDialog from '../shared/ApiKeyRevealDialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import PaginationBar from '../PlatformAdmin/PaginationBar';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

const fullName = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User ${u.id}`;

const USER_ERRORS = [
  { status: 403, match: /higher privileges/i, message: PARTNER_ADMIN_COPY.higherTierUser },
];

// Roles a company admin may never hand out here: company_admin is granted by the
// platform or the partner; partner_admin only exists for a partner's own staff.
const NOT_GRANTABLE = [ROLE_NAMES.COMPANY_ADMIN, ROLE_NAMES.PARTNER_ADMIN];

/**
 * A user of a higher tier can't be managed by a company admin (Partner tier
 * guide §7.2): the backend 403s. Returns the reason to show, or null.
 */
const higherTierReason = (user) => {
  const roles = user.roles || [];
  if (
    roles.some(
      (r) => r.name === ROLE_NAMES.PLATFORM_ADMIN || r.name === ROLE_NAMES.PLATFORM_STAFF || r.scope === 'platform'
    )
  ) {
    return ROLE_COPY.managedByPlatform;
  }
  if (roles.some((r) => r.name === ROLE_NAMES.PARTNER_ADMIN || r.scope === 'partner')) {
    return ROLE_COPY.managedByPartner;
  }
  return null;
};

/**
 * Company Admin users panel (spec §5.1). Scoped to the admin's own company.
 * company_admin is shown as a read-only badge and cannot be assigned/removed here.
 * Rows of platform- or partner-tier users are read-only (Partner tier guide §7.2).
 */
const CompanyUsersPanel = ({ notify }) => {
  const { has, ready: permissionsReady } = usePermissions();
  // The role directory (/company-admin/roles) is gated by role_management, not
  // user_management (spec §3) — a users-only admin would 403, so skip the fetch
  // and let the pickers explain instead of toasting an error on every visit.
  const canListRoles = permissionsReady && has(PERMISSIONS.ROLE_MANAGEMENT);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', role_ids: [] });
  const [creating, setCreating] = useState(false);

  const [keyReveal, setKeyReveal] = useState(null);

  const [roleTarget, setRoleTarget] = useState(null);
  const [roleToAdd, setRoleToAdd] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyAdminService.listUsers({
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setUsers(data?.users || []);
      setTotal(Number.isFinite(data?.total) ? data.total : (data?.users || []).length);
    } catch (err) {
      setError(err?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, offset]);

  const loadRoles = useCallback(async () => {
    if (!canListRoles) {
      setRoles([]);
      return;
    }
    try {
      // Roles feed the pickers — ask for a big page (max 200).
      const data = await companyAdminService.listRoles({ limit: 200 });
      setRoles(Array.isArray(data) ? data : data?.roles || []);
    } catch (err) {
      notify(err?.message || 'Failed to load roles.', 'error');
    }
  }, [notify, canListRoles]);

  // Separate effects so a search/page change re-fetches users only (not roles).
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Assign picker: any grantable role the user doesn't already hold — includes
  // company_member so it can be re-added after removal.
  const assignableRoles = useMemo(() => {
    if (!roleTarget) return [];
    const held = new Set((roleTarget.roles || []).map((r) => r.id));
    return roles.filter((r) => !NOT_GRANTABLE.includes(r.name) && !held.has(r.id));
  }, [roleTarget, roles]);

  // Create picker: grantable roles minus company_member (auto-assigned on creation).
  const createRoleOptions = useMemo(
    () => roles.filter((r) => !NOT_GRANTABLE.includes(r.name) && r.name !== ROLE_NAMES.COMPANY_MEMBER),
    [roles]
  );

  const handleCreate = async () => {
    if (!form.email.trim()) return;
    try {
      setCreating(true);
      const res = await companyAdminService.createUser({
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role_ids: form.role_ids,
      });
      setCreateOpen(false);
      setForm({ email: '', first_name: '', last_name: '', role_ids: [] });
      setKeyReveal({
        apiKey: res?.api_key,
        roles: res?.roles || [],
        title: 'User created',
        subtitle: `Copy ${res?.user?.email || 'the user'}'s API key now — it will not be shown again.`,
      });
      await loadUsers();
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Failed to create user.'), 'error');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = (user) => {
    if (user.is_active) {
      setConfirm({
        type: 'deactivate',
        user,
        heading: `Deactivate ${user.email}?`,
        text: 'The user is signed out immediately and cannot log in until reactivated.',
        action: 'Deactivate',
      });
    } else {
      runUpdate(user.id, { is_active: true }, 'User reactivated.');
    }
  };

  const runUpdate = async (userId, patch, successMsg) => {
    try {
      setBusy(true);
      await companyAdminService.updateUser(userId, patch);
      notify(successMsg, 'success');
      setConfirm(null);
      await loadUsers();
    } catch (err) {
      // Surfaces "Cannot deactivate the last active company admin" / "You cannot
      // deactivate your own account" (spec §5.1).
      notify(mapApiError(err, USER_ERRORS, 'Update failed.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignRole = async () => {
    if (!roleTarget || !roleToAdd) return;
    try {
      setBusy(true);
      await companyAdminService.assignUserRole(roleTarget.id, roleToAdd);
      notify('Role assigned.', 'success');
      setRoleTarget(null);
      setRoleToAdd('');
      await loadUsers();
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Failed to assign role.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (confirm?.type === 'deactivate') {
      runUpdate(confirm.user.id, { is_active: false }, 'User deactivated.');
    } else if (confirm?.type === 'removeRole') {
      try {
        setBusy(true);
        await companyAdminService.removeUserRole(confirm.user.id, confirm.roleId);
        notify('Role removed.', 'info');
        setConfirm(null);
        await loadUsers();
      } catch (err) {
        notify(mapApiError(err, USER_ERRORS, 'Failed to remove role.'), 'error');
      } finally {
        setBusy(false);
      }
    } else if (confirm?.type === 'resetKey') {
      try {
        setBusy(true);
        const res = await companyAdminService.resetUserApiKey(confirm.user.id);
        setConfirm(null);
        setKeyReveal({
          apiKey: res?.api_key,
          title: 'API key reset',
          subtitle: `The old key for ${confirm.user.email} stopped working immediately. Copy the new key now.`,
        });
      } catch (err) {
        notify(mapApiError(err, USER_ERRORS, 'Failed to reset key.'), 'error');
      } finally {
        setBusy(false);
      }
    }
  };

  return (
    <>
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          className={styles.primaryBtn}
          onClick={() => setCreateOpen(true)}
        >
          New user
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : users.length === 0 ? (
        <Box className={styles.emptyState}>
          <Users className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No users yet</Typography>
          <Typography variant="body2">Invite your first teammate to get started.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {users.map((user) => {
            const lockedReason = higherTierReason(user);
            return (
            <Box key={user.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
              <Box className={styles.companyMain} sx={{ flex: '1 1 220px' }}>
                <span className={styles.companyAvatar}>
                  {(user.first_name || user.email || '?').charAt(0).toUpperCase()}
                </span>
                <Box>
                  <span className={styles.companyName}>{fullName(user)}</span>
                  <span className={styles.companyMeta}>{user.email}</span>
                </Box>
              </Box>

              <Box className={styles.roleChips} sx={{ flex: '1 1 200px' }}>
                {(user.roles || []).map((r) => (
                  <span
                    key={r.id}
                    className={`${styles.badge} ${
                      r.name === ROLE_NAMES.COMPANY_ADMIN || r.name === ROLE_NAMES.PARTNER_ADMIN
                        ? styles.badgeAdmin
                        : r.is_system
                        ? styles.badgeSystem
                        : ''
                    }`}
                  >
                    {r.name === ROLE_NAMES.COMPANY_ADMIN
                      ? ROLE_COPY.companyAdmin
                      : r.name === ROLE_NAMES.PARTNER_ADMIN
                      ? ROLE_COPY.partnerAdmin
                      : r.name}
                    {/* Company admins may remove any role EXCEPT company_admin (that
                        one is platform/partner-only → 403). company_member IS
                        removable. Nothing is removable on a higher-tier user. */}
                    {r.name !== ROLE_NAMES.COMPANY_ADMIN && !lockedReason && (
                      <X
                        size={12}
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          setConfirm({
                            type: 'removeRole',
                            user,
                            roleId: r.id,
                            heading: `Remove role "${r.name}"?`,
                            text:
                              r.name === 'company_member'
                                ? `Removes the default company_member role from ${user.email}. Their access becomes just their other roles ∩ the company's entitlements.`
                                : `Removes the ${r.name} role from ${user.email}.`,
                            action: 'Remove role',
                          })
                        }
                      />
                    )}
                  </span>
                ))}
              </Box>

              <span className={`${styles.statusPill} ${user.is_active ? styles.active : styles.inactive}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>

              <Box className={styles.actionsCell}>
                {/* Disabled controls need a span wrapper for the tooltip to show. */}
                <Tooltip title={lockedReason || 'Assign role'}>
                  <span>
                    <IconButton
                      size="small"
                      className={styles.subtleBtn}
                      disabled={!!lockedReason}
                      onClick={() => { setRoleTarget(user); setRoleToAdd(''); }}
                    >
                      <ShieldPlus size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={lockedReason || 'Reset API key'}>
                  <span>
                    <IconButton
                      size="small"
                      className={styles.subtleBtn}
                      disabled={!!lockedReason}
                      onClick={() =>
                        setConfirm({
                          type: 'resetKey',
                          user,
                          heading: `Reset API key for ${user.email}?`,
                          text: 'The current key stops working immediately. The new key is shown once.',
                          action: 'Reset key',
                        })
                      }
                    >
                      <KeyRound size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={lockedReason || (user.is_active ? 'Deactivate' : 'Activate')}>
                  <span>
                    <IconButton
                      size="small"
                      className={styles.dangerIcon}
                      disabled={!!lockedReason}
                      onClick={() => toggleActive(user)}
                    >
                      {user.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            );
          })}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="users"
          onOffsetChange={setOffset}
        />
      )}

      {/* Create user */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>New user</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus label="Email" type="email" required fullWidth variant="outlined"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={styles.formField}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name" fullWidth variant="outlined"
              value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="Last name" fullWidth variant="outlined"
              value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className={styles.formField}
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              label="Roles"
              value={form.role_ids}
              onChange={(e) => setForm({ ...form, role_ids: e.target.value })}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) =>
                createRoleOptions.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')
              }
            >
              {createRoleOptions.length === 0 && (
                <MenuItem disabled>
                  {canListRoles
                    ? 'No roles available'
                    : 'Listing roles requires the Role Management permission'}
                </MenuItem>
              )}
              {createRoleOptions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  <Checkbox checked={form.role_ids.includes(r.id)} />
                  <ListItemText primary={r.name} secondary={r.is_system ? 'system' : 'custom'} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            company_member is assigned automatically.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !form.email.trim()} className={styles.primaryBtn}>
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create user'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign role */}
      <Dialog open={!!roleTarget} onClose={() => !busy && setRoleTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Assign role</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Grant a role to {roleTarget?.email}.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={roleToAdd} onChange={(e) => setRoleToAdd(e.target.value)}>
              {assignableRoles.length === 0 && (
                <MenuItem disabled>
                  {canListRoles
                    ? 'No roles available to add'
                    : 'Listing roles requires the Role Management permission'}
                </MenuItem>
              )}
              {assignableRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}{r.is_system ? ' (system)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRoleTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignRole} disabled={busy || !roleToAdd} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic confirm */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>{confirm?.action}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>{confirm?.heading}</Typography>
          <Typography className={styles.deleteText}>{confirm?.text}</Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runConfirm}
            disabled={busy}
            className={styles.dangerButton}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {busy ? 'Working...' : confirm?.action}
          </Button>
        </DialogActions>
      </Dialog>

      <ApiKeyRevealDialog
        open={!!keyReveal}
        apiKey={keyReveal?.apiKey}
        roles={keyReveal?.roles}
        title={keyReveal?.title}
        subtitle={keyReveal?.subtitle}
        onClose={() => setKeyReveal(null)}
      />
    </>
  );
};

export default CompanyUsersPanel;
